/**
 * Canonical Task Editor — same UI for create and edit (ClickUp-style).
 * mode: 'create' | 'edit'
 * Create: empty fields, save creates task.
 * Edit: existing task loaded, save updates task.
 * Layout, fields, and design are identical in both modes.
 */

import { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  CheckSquare,
  CircleDot,
  Calendar,
  User,
  Flag,
  Tag,
  Eye,
  EyeOff,
  MoreHorizontal,
  Trash2,
  Download,
  ChevronDown,
  ListTodo,
  Link2,
  CheckCheck,
  Paperclip,
  Upload,
  Bell,
  BellRing,
  Clock,
  Share2,
  Copy,
  Check,
  Loader2,
  X,
  ArrowUpRight,
  Plus,
  FolderOutput,
  Merge,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { AssigneePicker } from '@/components/tasks/assignee-picker';
import { TaskAssigneesCell } from '@/components/tasks/assignees-display';
import { getTaskAssignees } from '@/lib/task-assignees';
import { TagPicker } from '@/components/tasks/tag-picker';
import { TaskComments } from '@/components/tasks/task-comments';
import { CustomFieldsSection } from './CustomFieldsSection';
import {
  useTask,
  useTaskHistory,
  useCreateTask,
  useUpdateTask,
  useDeleteTask,
  useCreateSubtask,
  useFollowTask,
  useUnfollowTask,
} from '@/hooks/use-tasks';
import { useLists, useListStatuses } from '@/hooks/use-lists';
import { useComments } from '@/hooks/use-comments';
import { useAttachments, useUploadAttachment, useDeleteAttachment } from '@/hooks/use-attachments';
import axios from 'axios';
import {
  filesApi,
  listsApi,
  normalizeError,
  tasksApi,
  type ListTaskDuplicateMatch,
  type ListTaskDuplicatesResponse,
} from '@/lib/api';
import { useCreateListCustomField, useCreateProjectCustomField } from '@/hooks/use-custom-fields';

import { useMyCapabilities } from '@/hooks/use-permissions';
import { formatDisplayDate } from '@/lib/date-format';
import { cn, getInitials, getPriorityHexColor, getContrastTextColor, formatRelativeTime, formatActivityTimestamp, formatFileSize } from '@/lib/utils';
import { toast } from '@/components/ui/toaster';
import { useUIStore } from '@/stores/ui-store';
import { MoveTaskDialog } from './MoveTaskDialog';
import { MergeTicketDialog } from './MergeTicketDialog';
import type { TaskStatus, TaskPriority, TagMinimal } from '@/types';
import {
  ATTACHMENT_FILE_INPUT_ACCEPT,
  validateAttachmentFileClient,
} from '@/lib/attachment-upload-policy';

function summarizeUploadFailures(results: PromiseSettledResult<unknown>[]): string | undefined {
  const msgs: string[] = [];
  for (const r of results) {
    if (r.status === 'rejected') {
      msgs.push(normalizeError(r.reason).message);
    }
  }
  if (!msgs.length) return undefined;
  const uniq = [...new Set(msgs)];
  if (uniq.length === 1) return uniq[0];
  if (uniq.length === 2) return `${uniq[0]} ${uniq[1]}`;
  return `${uniq[0]} (+${uniq.length - 1} more)`;
}

const borderCls = 'border-border';
const bgCard = 'bg-card';
const bgPage = 'bg-background';

export type UnifiedTaskEditorMode = 'create' | 'edit';

/** Modal state for edit-mode attachment uploads (safety UX). */
type EditAttachmentUploadModal =
  | null
  | { phase: 'uploading'; total: number }
  | { phase: 'result'; total: number; succeeded: number; failed: number; failureHint?: string };

export interface UnifiedTaskEditorCreateProps {
  mode: 'create';
  projectId: string;
  listId: string;
  statuses: TaskStatus[];
  preselectedStatusId?: string;
  projectName?: string;
  listName?: string;
  onClose?: () => void;
  onCreated?: (taskId: string) => void;
}

export interface UnifiedTaskEditorEditProps {
  mode: 'edit';
  taskId: string;
  /** When true, show close button instead of back (e.g. in panel/dialog) */
  embedded?: boolean;
  onClose?: () => void;
}

export type UnifiedTaskEditorProps = UnifiedTaskEditorCreateProps | UnifiedTaskEditorEditProps;

export function UnifiedTaskEditor(props: UnifiedTaskEditorProps) {
  const isCreate = props.mode === 'create';
  const isEmbedded = !isCreate && Boolean(props.embedded);
  const navigate = useNavigate();
  const { data: capabilities } = useMyCapabilities();
  const canDeleteTask = Boolean(capabilities?.['task.delete']);
  const canAssignTask = Boolean(capabilities?.['task.assign']);
  const canCreateSubtask = Boolean(capabilities?.['task.subtask.create']);
  const canManageProjectCustomFields = Boolean(capabilities?.['custom_fields.project.manage']);
  const canManageListCustomFields = Boolean(capabilities?.['custom_fields.list.manage']);
  const canEditTaskCustomFields = Boolean(capabilities?.['custom_fields.task.edit']);
  const canApplyTags = Boolean(capabilities?.['task.tags.apply']);
  const canUploadAttachments = Boolean(capabilities?.['attachment.upload']);
  const canEdit = Boolean(capabilities?.['task.edit']);
  const canMergeList = Boolean(capabilities?.['task.merge_list']);
  const canMergeSitewide = Boolean(capabilities?.['task.merge_sitewide']);
  const canMerge = canMergeList || canMergeSitewide;
  const canViewHistory = Boolean(capabilities?.['task.history.view']);

  // Edit mode: fetch task
  const { data: task, isLoading: taskLoading, error: taskError } = useTask(
    isCreate ? undefined : props.taskId
  );
  const statusesFromList = useListStatuses(isCreate ? undefined : task?.listId);
  const statuses = isCreate ? props.statuses : (statusesFromList.data ?? []);
  const sortedStatuses = [...statuses].sort((a, b) => a.position - b.position);
  const preselectedStatusId = isCreate ? (props as UnifiedTaskEditorCreateProps).preselectedStatusId : undefined;
  const firstSortedStatusId = sortedStatuses[0]?.id ?? '';

  // Create mode: local state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [statusId, setStatusId] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [priority, setPriority] = useState<TaskPriority>('low');
  const [assigneeIds, setAssigneeIds] = useState<string[]>([]);
  const [assignedTeamId, setAssignedTeamId] = useState<string | null>(null);
  const [isClientVisible, setIsClientVisible] = useState(true);
  const [fieldsOpen, setFieldsOpen] = useState(true);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [showMoveDialog, setShowMoveDialog] = useState(false);
  const [showMergeDialog, setShowMergeDialog] = useState(false);
  const [addSubtaskOpen, setAddSubtaskOpen] = useState(false);
  const [subtaskTitle, setSubtaskTitle] = useState('');
  const [subtaskListId, setSubtaskListId] = useState('');
  const [subtaskStatusId, setSubtaskStatusId] = useState('');
  const [subtaskPriority, setSubtaskPriority] = useState<TaskPriority>('low');
  const [subtaskAssigneeIds, setSubtaskAssigneeIds] = useState<string[]>([]);
  const [subtaskAssignedTeamId, setSubtaskAssignedTeamId] = useState<string | null>(null);
  const [subtaskDueDate, setSubtaskDueDate] = useState('');
  const [createDraftTags, setCreateDraftTags] = useState<TagMinimal[]>([]);
  const [createCustomFieldValues, setCreateCustomFieldValues] = useState<Record<string, unknown>>({});
  const [pendingAttachmentFiles, setPendingAttachmentFiles] = useState<File[]>([]);
  const [isUploadingPendingFiles, setIsUploadingPendingFiles] = useState(false);
  /** Edit mode: batch upload parity with create (multi-file + clear in-flight UI). */
  const [isUploadingEditAttachments, setIsUploadingEditAttachments] = useState(false);
  /** Count of files in the current edit-mode upload batch (for visible status in attachment area). */
  const [editUploadBatchTotal, setEditUploadBatchTotal] = useState(0);
  /** Blocks leaving the editor until the user acknowledges the upload result (edit-mode attachments only). */
  const [editAttachmentUploadModal, setEditAttachmentUploadModal] = useState<EditAttachmentUploadModal>(null);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [duplicateMatches, setDuplicateMatches] = useState<ListTaskDuplicateMatch[]>([]);
  const [selectedDuplicateTaskId, setSelectedDuplicateTaskId] = useState<string | null>(null);
  const [duplicateDialogTrigger, setDuplicateDialogTrigger] = useState<'blur' | 'submit'>('blur');
  const [duplicateNoticeTitle, setDuplicateNoticeTitle] = useState<string | null>(null);
  const [duplicateSuppressedTitle, setDuplicateSuppressedTitle] = useState<string | null>(null);
  const [isCheckingDuplicates, setIsCheckingDuplicates] = useState(false);
  const pendingFilesInputRef = useRef<HTMLInputElement>(null);
  const duplicateCheckCacheRef = useRef<Map<string, ListTaskDuplicatesResponse>>(
    new Map<string, ListTaskDuplicatesResponse>(),
  );

  const projectId = isCreate ? (props as UnifiedTaskEditorCreateProps).projectId : task?.projectId ?? '';
  const listId = isCreate ? (props as UnifiedTaskEditorCreateProps).listId : task?.listId ?? '';
  const projectName = isCreate ? (props as UnifiedTaskEditorCreateProps).projectName : task?.project?.name;
  const listName = isCreate ? (props as UnifiedTaskEditorCreateProps).listName : task?.list?.name;

  const createTask = useCreateTask();
  const updateTask = useUpdateTask();
  const deleteTask = useDeleteTask();
  const followTask = useFollowTask();
  const unfollowTask = useUnfollowTask();
  const createSubtask = useCreateSubtask(isCreate ? undefined : props.taskId);
  const createListCustomField = useCreateListCustomField(listId || '');
  const createProjectCustomField = useCreateProjectCustomField(projectId || '');
  const { data: comments = [] } = useComments(isCreate ? '' : props.taskId);
  const { data: history = [] } = useTaskHistory(isCreate ? '' : props.taskId, canViewHistory);
  const { data: attachments = [] } = useAttachments(isCreate ? '' : props.taskId);
  const uploadAttachment = useUploadAttachment();
  const deleteAttachment = useDeleteAttachment();

  const editAttachmentLeaveBlocked = editAttachmentUploadModal !== null;

  const notifyEditAttachmentLeaveBlocked = () => {
    toast({
      title: 'Upload may not be complete yet. Please wait until the completion message appears.',
      variant: 'destructive',
    });
  };

  useEffect(() => {
    if (!isUploadingEditAttachments) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [isUploadingEditAttachments]);

  const setTaskPanelAttachmentUploadBarrier = useUIStore((s) => s.setTaskPanelAttachmentUploadBarrier);

  // Sync barrier with modal state. Do not clear barrier in a per-update cleanup: when `modal` goes
  // from null → non-null, React runs the previous cleanup before the next effect body, which would
  // briefly set the barrier false and allow TaskPanel backdrop to close without a toast.
  useLayoutEffect(() => {
    if (isCreate) {
      setTaskPanelAttachmentUploadBarrier(false);
      return;
    }
    setTaskPanelAttachmentUploadBarrier(editAttachmentUploadModal !== null);
  }, [isCreate, editAttachmentUploadModal, setTaskPanelAttachmentUploadBarrier]);

  useEffect(() => {
    return () => setTaskPanelAttachmentUploadBarrier(false);
  }, [setTaskPanelAttachmentUploadBarrier]);

  const normalizeDuplicateTitle = (value: string): string =>
    value.trim().replace(/\s+/g, ' ').toLowerCase();

  const getDuplicateKey = (targetListId: string, normalizedTitle: string): string =>
    `${targetListId}:${normalizedTitle}`;

  const findSelectedDuplicateMatch = (): ListTaskDuplicateMatch | undefined =>
    duplicateMatches.find((m) => m.id === selectedDuplicateTaskId) ?? duplicateMatches[0];

  const getOpenTaskIdForDuplicate = (match: ListTaskDuplicateMatch | undefined): string | null => {
    if (!match) return null;
    if (match.isMerged) return match.mergedInto?.id ?? null;
    return match.id;
  };

  const checkDuplicateTitle = async (
    targetListId: string,
    rawTitle: string,
    forceFresh = false,
  ): Promise<{ normalizedTitle: string; response: ListTaskDuplicatesResponse }> => {
    const normalizedTitle = normalizeDuplicateTitle(rawTitle);
    if (!normalizedTitle) {
      return { normalizedTitle, response: { hasDuplicate: false, matches: [] } };
    }

    const cacheKey = getDuplicateKey(targetListId, normalizedTitle);
    if (!forceFresh) {
      const cached = duplicateCheckCacheRef.current.get(cacheKey);
      if (cached) {
        return { normalizedTitle, response: cached };
      }
    }

    const apiResult = await listsApi.getTaskDuplicates(targetListId, rawTitle.trim());
    duplicateCheckCacheRef.current.set(cacheKey, apiResult.data);
    return { normalizedTitle, response: apiResult.data };
  };

  // Reset local form when creating (or when create defaults change)
  useEffect(() => {
    if (!isCreate) return;
    setTitle('');
    setDescription('');
    setDueDate('');
    setPriority('low');
    setAssigneeIds([]);
    setAssignedTeamId(null);
    setIsClientVisible(true);
    setIsEditingTitle(true);
    setIsEditingDescription(false);
    setStatusId(preselectedStatusId || firstSortedStatusId || '');
    setCreateDraftTags([]);
    setCreateCustomFieldValues({});
    setPendingAttachmentFiles([]);
    setDuplicateDialogOpen(false);
    setDuplicateMatches([]);
    setSelectedDuplicateTaskId(null);
    setDuplicateDialogTrigger('blur');
    setDuplicateNoticeTitle(null);
    setDuplicateSuppressedTitle(null);
    setIsCheckingDuplicates(false);
    duplicateCheckCacheRef.current.clear();
  }, [isCreate, preselectedStatusId, firstSortedStatusId]);

  useEffect(() => {
    if (!isCreate || canApplyTags) return;
    setCreateDraftTags([]);
  }, [isCreate, canApplyTags]);

  useEffect(() => {
    if (!isCreate) return;
    const normalizedNow = normalizeDuplicateTitle(title);
    if (duplicateNoticeTitle && normalizedNow !== duplicateNoticeTitle) {
      setDuplicateNoticeTitle(null);
      setDuplicateMatches([]);
      setSelectedDuplicateTaskId(null);
      setDuplicateDialogOpen(false);
    }
    if (duplicateSuppressedTitle && normalizedNow !== duplicateSuppressedTitle) {
      setDuplicateSuppressedTitle(null);
    }
  }, [isCreate, title, duplicateNoticeTitle, duplicateSuppressedTitle]);

  // Sync local form from loaded task in edit mode
  useEffect(() => {
    if (isCreate || !task) return;
    setTitle(task.title);
    setDescription(task.description || '');
    setStatusId(task.statusId);
    setDueDate(task.dueDate?.split('T')[0] || '');
    setPriority(task.priority as TaskPriority);
    setAssigneeIds(getTaskAssignees(task).map((a) => a.id));
    setAssignedTeamId(task.assignedTeamId);
    setIsClientVisible(task.isClientVisible);
  }, [isCreate, task]);

  const { data: projectLists = [] } = useLists(projectId && addSubtaskOpen ? projectId : undefined);
  const { data: subtaskListStatuses = [] } = useListStatuses(subtaskListId || listId);
  const sortedSubtaskStatuses = [...subtaskListStatuses].sort((a, b) => a.position - b.position);

  useEffect(() => {
    if (addSubtaskOpen && task) {
      setSubtaskListId(task.listId);
      setSubtaskTitle('');
      setSubtaskPriority('low');
      setSubtaskAssigneeIds([]);
      setSubtaskAssignedTeamId(null);
      setSubtaskDueDate('');
    }
  }, [addSubtaskOpen, task]);

  useEffect(() => {
    if (subtaskListId && sortedSubtaskStatuses.length > 0 && !sortedSubtaskStatuses.find((s) => s.id === subtaskStatusId)) {
      setSubtaskStatusId(sortedSubtaskStatuses[0].id);
    } else if (subtaskListId && sortedSubtaskStatuses.length > 0 && !subtaskStatusId) {
      setSubtaskStatusId(sortedSubtaskStatuses[0].id);
    }
  }, [subtaskListId, sortedSubtaskStatuses, subtaskStatusId]);

  const handleAddSubtask = async () => {
    if (!task || !subtaskTitle.trim()) {
      toast({ title: 'Title is required', variant: 'destructive' });
      return;
    }
    if (!subtaskStatusId) {
      toast({ title: 'Please select a status', variant: 'destructive' });
      return;
    }
    try {
      await createSubtask.mutateAsync({
        title: subtaskTitle.trim(),
        listId: subtaskListId || task.listId,
        statusId: subtaskStatusId,
        priority: subtaskPriority,
        assigneeIds: subtaskAssigneeIds.length > 0 ? subtaskAssigneeIds : undefined,
        assignedTeamId: subtaskAssignedTeamId || undefined,
        dueDate: subtaskDueDate || undefined,
      });
      toast({ title: 'Subtask created' });
      setAddSubtaskOpen(false);
    } catch {
      toast({ title: 'Failed to create subtask', variant: 'destructive' });
    }
  };

  const runCreateTask = async (skipDuplicateCheck: boolean) => {
    const createTitle = title.trim();
    const createListId = (props as UnifiedTaskEditorCreateProps).listId;
    const normalizedCreateTitle = normalizeDuplicateTitle(createTitle);

    if (!skipDuplicateCheck) {
      const duplicateCheck = await checkDuplicateTitle(createListId, createTitle, true);
      if (
        duplicateCheck.response.hasDuplicate &&
        duplicateCheck.response.matches.length > 0 &&
        duplicateSuppressedTitle !== normalizedCreateTitle
      ) {
        setDuplicateMatches(duplicateCheck.response.matches);
        setSelectedDuplicateTaskId(duplicateCheck.response.matches[0]?.id ?? null);
        setDuplicateDialogTrigger('submit');
        setDuplicateNoticeTitle(normalizedCreateTitle);
        setDuplicateDialogOpen(true);
        return;
      }
    }

    const created = await createTask.mutateAsync({
      projectId: (props as UnifiedTaskEditorCreateProps).projectId,
      data: {
        listId: createListId,
        statusId,
        title: createTitle,
        description: description.trim() || undefined,
        priority,
        assigneeIds: assigneeIds.length > 0 ? assigneeIds : undefined,
        assignedTeamId: assignedTeamId || undefined,
        dueDate: dueDate || undefined,
        isClientVisible,
        tagIds:
          canApplyTags && createDraftTags.length > 0
            ? createDraftTags.map((t) => t.id)
            : undefined,
      },
    });

    if (canEditTaskCustomFields && Object.keys(createCustomFieldValues).length > 0) {
      try {
        await tasksApi.updateCustomFields(created.id, createCustomFieldValues);
      } catch (e) {
        const err = normalizeError(e);
        toast({
          title: 'Task created, but custom field values could not be saved',
          description: err.message,
          variant: 'destructive',
        });
      }
    }

    toast({ title: 'Task created' });

    if (pendingAttachmentFiles.length > 0 && canUploadAttachments) {
      setIsUploadingPendingFiles(true);
      const valid: File[] = [];
      const clientErrors: string[] = [];
      for (const f of pendingAttachmentFiles) {
        const err = validateAttachmentFileClient(f);
        if (err) clientErrors.push(`${f.name}: ${err}`);
        else valid.push(f);
      }
      if (clientErrors.length) {
        toast({
          title:
            clientErrors.length === pendingAttachmentFiles.length
              ? 'Cannot upload attachments'
              : 'Some attachments were skipped',
          description: clientErrors.slice(0, 5).join('\n'),
          variant: 'destructive',
        });
      }
      if (valid.length === 0) {
        setIsUploadingPendingFiles(false);
      } else {
        const results = await Promise.allSettled(
          valid.map((file) => uploadAttachment.mutateAsync({ taskId: created.id, file })),
        );
        const failed = results.filter((r) => r.status === 'rejected').length;
        const failureHint = summarizeUploadFailures(results);
        setIsUploadingPendingFiles(false);
        if (failed > 0) {
          toast({
            title:
              failed === valid.length
                ? 'Task created, but all attachment uploads failed'
                : `Task created; ${failed} attachment upload(s) failed`,
            description: failureHint,
            variant: 'destructive',
          });
        } else {
          toast({ title: `${valid.length} file(s) attached` });
        }
      }
      setPendingAttachmentFiles([]);
    }

    (props as UnifiedTaskEditorCreateProps).onCreated?.(created.id);
    (props as UnifiedTaskEditorCreateProps).onClose?.();
    setDuplicateSuppressedTitle(null);
  };

  const handleSaveCreate = async () => {
    if (!title.trim()) {
      toast({ title: 'Title is required', variant: 'destructive' });
      return;
    }
    if (!statusId) {
      toast({ title: 'Please select a status', variant: 'destructive' });
      return;
    }
    try {
      await runCreateTask(false);
    } catch (e) {
      const err = normalizeError(e);
      toast({
        title: err.message || 'Failed to create task',
        variant: 'destructive',
      });
    }
  };

  const handleCreateAnyway = async () => {
    const normalizedCurrentTitle = normalizeDuplicateTitle(title);
    setDuplicateSuppressedTitle(normalizedCurrentTitle || null);
    setDuplicateDialogOpen(false);

    if (duplicateDialogTrigger === 'blur') {
      return;
    }

    try {
      await runCreateTask(true);
    } catch (e) {
      const err = normalizeError(e);
      toast({
        title: err.message || 'Failed to create task',
        variant: 'destructive',
      });
    }
  };

  const handleOpenExistingTask = () => {
    const selectedMatch = findSelectedDuplicateMatch();
    const targetTaskId = getOpenTaskIdForDuplicate(selectedMatch);
    if (!targetTaskId) return;
    setDuplicateDialogOpen(false);
    (props as UnifiedTaskEditorCreateProps).onClose?.();
    navigate(`/tasks/${targetTaskId}`);
  };

  const handleDuplicateTitleBlurCheck = async () => {
    if (!isCreate) return;
    const createListId = (props as UnifiedTaskEditorCreateProps).listId;
    const createTitle = title.trim();
    const normalizedTitle = normalizeDuplicateTitle(createTitle);
    if (!normalizedTitle) return;
    if (duplicateSuppressedTitle === normalizedTitle) return;

    try {
      setIsCheckingDuplicates(true);
      const duplicateCheck = await checkDuplicateTitle(createListId, createTitle, false);
      if (duplicateCheck.response.hasDuplicate && duplicateCheck.response.matches.length > 0) {
        setDuplicateMatches(duplicateCheck.response.matches);
        setSelectedDuplicateTaskId(duplicateCheck.response.matches[0]?.id ?? null);
        setDuplicateDialogTrigger('blur');
        setDuplicateNoticeTitle(duplicateCheck.normalizedTitle);
        setDuplicateDialogOpen(true);
      } else if (duplicateNoticeTitle === normalizedTitle) {
        setDuplicateNoticeTitle(null);
        setDuplicateMatches([]);
        setSelectedDuplicateTaskId(null);
      }
    } catch {
      // Do not block the create form if pre-check fails.
    } finally {
      setIsCheckingDuplicates(false);
    }
  };

  const handleTitleBlur = () => {
    if (isCreate) {
      void handleDuplicateTitleBlurCheck();
      return;
    }
    if (title.trim() && task && title !== task.title) {
      updateTask.mutate({
        id: task.id,
        projectId: task.projectId,
        data: { title: title.trim() },
      });
    }
    setIsEditingTitle(false);
  };

  const handleDescriptionBlur = () => {
    if (isCreate) return;
    if (task && description !== (task.description || '')) {
      updateTask.mutate({
        id: task.id,
        projectId: task.projectId,
        data: { description: description || null },
      });
    }
    setIsEditingDescription(false);
  };

  const handleStatusChange = (val: string) => {
    setStatusId(val);
    if (!isCreate && task) {
      updateTask.mutate({
        id: task.id,
        projectId: task.projectId,
        data: { statusId: val },
      });
    }
  };

  const handlePriorityChange = (val: string) => {
    setPriority(val as TaskPriority);
    if (!isCreate && task) {
      updateTask.mutate({
        id: task.id,
        projectId: task.projectId,
        data: { priority: val },
      });
    }
  };

  const handleAssigneeChange = (value: { assigneeIds: string[]; assignedTeamId: string | null }) => {
    setAssigneeIds(value.assigneeIds);
    setAssignedTeamId(value.assignedTeamId);
    if (!isCreate && task) {
      updateTask.mutate({
        id: task.id,
        projectId: task.projectId,
        data: { assigneeIds: value.assigneeIds, assignedTeamId: value.assignedTeamId },
      });
    }
  };

  const handleDueDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value || '';
    setDueDate(val);
    if (!isCreate && task) {
      updateTask.mutate({
        id: task.id,
        projectId: task.projectId,
        data: { dueDate: val || null },
      });
    }
  };

  const handleVisibilityToggle = () => {
    setIsClientVisible((v) => !v);
    if (!isCreate && task) {
      updateTask.mutate({
        id: task.id,
        projectId: task.projectId,
        data: { isClientVisible: !task.isClientVisible },
      });
    }
  };

  const handleDelete = () => {
    if (editAttachmentLeaveBlocked) {
      notifyEditAttachmentLeaveBlocked();
      return;
    }
    if (!task || !window.confirm('Are you sure you want to delete this task?')) return;
    deleteTask.mutate(
      { id: task.id, projectId: task.projectId },
      {
        onSuccess: () =>
          navigate(
            task.listId
              ? `/projects/${task.projectId}/lists/${task.listId}`
              : `/projects/${task.projectId}`,
          ),
      },
    );
  };

  const handleShareLink = async () => {
    if (isCreate) return;
    const url = `${window.location.origin}/tasks/${task?.id}`;
    try {
      await navigator.clipboard.writeText(url);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch (err) {
      console.error(err);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const raw = input.files;

    if (isCreate || !canUploadAttachments) {
      input.value = '';
      return;
    }
    const tid = props.taskId;
    if (!raw?.length || !tid) {
      input.value = '';
      return;
    }

    // Snapshot files before resetting the input — clearing `value` first can empty the
    // FileList in some browsers, which skipped uploads and made upload status never appear.
    const files = Array.from(raw);
    input.value = '';

    const valid: File[] = [];
    const clientErrors: string[] = [];
    for (const f of files) {
      const localErr = validateAttachmentFileClient(f);
      if (localErr) {
        clientErrors.push(`${f.name}: ${localErr}`);
      } else {
        valid.push(f);
      }
    }
    if (clientErrors.length) {
      toast({
        title: clientErrors.length === files.length ? 'Cannot upload' : 'Some files were skipped',
        description: clientErrors.slice(0, 5).join('\n'),
        variant: 'destructive',
      });
    }
    if (valid.length === 0) {
      return;
    }

    setEditUploadBatchTotal(valid.length);
    setIsUploadingEditAttachments(true);
    setEditAttachmentUploadModal({ phase: 'uploading', total: valid.length });
    try {
      const results = await Promise.allSettled(
        valid.map((file) => uploadAttachment.mutateAsync({ taskId: tid, file })),
      );
      const failed = results.filter((r) => r.status === 'rejected').length;
      const succeeded = valid.length - failed;
      const failureHint = summarizeUploadFailures(results);
      setEditAttachmentUploadModal({
        phase: 'result',
        total: valid.length,
        succeeded,
        failed,
        failureHint,
      });
      if (failed > 0) {
        toast({
          title:
            failed === valid.length
              ? 'All attachment uploads failed'
              : `${failed} attachment upload(s) failed`,
          description: failureHint,
          variant: 'destructive',
        });
      } else {
        toast({ title: `${valid.length} file(s) attached` });
      }
    } finally {
      setIsUploadingEditAttachments(false);
      setEditUploadBatchTotal(0);
    }
  };

  const handlePendingFilesSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = e.target.files;
    if (!picked?.length) return;
    const additions: File[] = [];
    for (const f of Array.from(picked)) {
      const err = validateAttachmentFileClient(f);
      if (err) {
        toast({ title: f.name, description: err, variant: 'destructive' });
      } else {
        additions.push(f);
      }
    }
    if (additions.length) {
      setPendingAttachmentFiles((prev) => [...prev, ...additions]);
    }
    e.target.value = '';
  };

  const removePendingFile = (index: number) => {
    setPendingAttachmentFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleFileDownload = async (att: { id: string; fileName: string }) => {
    try {
      const { data } = await filesApi.getRemoteDownloadInfo(att.id);
      if (data?.url) {
        if (data.openMode === 'new-tab') {
          window.open(data.url, '_blank', 'noopener,noreferrer');
        } else {
          const link = document.createElement('a');
          link.href = data.url;
          link.download = data.fileName || att.fileName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
        return;
      }
      toast({ title: 'Download URL unavailable', variant: 'destructive' });
      return;
    } catch (e) {
      if (!axios.isAxiosError(e) || e.response?.status !== 400) {
        toast({ title: 'Failed to download file', variant: 'destructive' });
        return;
      }
    }
    try {
      const response = await filesApi.download(att.id);
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = att.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch {
      toast({ title: 'Failed to download file', variant: 'destructive' });
    }
  };

  const nextFieldKey = (prefix: string): string =>
    `${prefix}_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

  const handleCreateCustomField = async (
    scope: 'list' | 'project',
    type: 'text' | 'longText' | 'number'
  ) => {
    if (!projectId || !listId) return;
    const isLongText = type === 'longText';
    const payload =
      type === 'number'
        ? { name: 'Number', key: nextFieldKey('number'), type: 'number' as const }
        : isLongText
          ? {
              name: 'Long text',
              key: nextFieldKey('long_text'),
              type: 'text' as const,
              config: { longText: true },
            }
          : { name: 'Text', key: nextFieldKey('text'), type: 'text' as const };

    try {
      if (scope === 'list') {
        await createListCustomField.mutateAsync(payload);
      } else {
        await createProjectCustomField.mutateAsync(payload);
      }
      toast({ title: `${scope === 'list' ? 'List' : 'Project'} field created` });
    } catch {
      toast({ title: 'Could not create field', variant: 'destructive' });
    }
  };

  // Edit mode: loading / error / not found
  if (!isCreate) {
    if (taskLoading) {
      return (
        <div className={cn('flex h-full', bgPage)}>
          <div className="flex-1 p-6 flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </div>
      );
    }
    if (taskError || !task) {
      return (
        <div className={cn('flex h-full flex-col items-center justify-center gap-4 p-6', bgPage)}>
          <CheckSquare className="h-12 w-12 text-muted-foreground/50" />
          <p className="text-lg font-medium">Task not found</p>
          <Button variant="outline" onClick={() => navigate('/dashboard')}>
            Back to Dashboard
          </Button>
        </div>
      );
    }
  }

  const currentStatus = sortedStatuses.find((s) => s.id === statusId);
  const displayTitle = isCreate ? title || 'New task' : title;
  const normalizedCurrentTitle = normalizeDuplicateTitle(title);
  const selectedDuplicateMatch = findSelectedDuplicateMatch();
  const selectedDuplicateTargetTaskId = getOpenTaskIdForDuplicate(selectedDuplicateMatch);
  const hasMergedDuplicate = duplicateMatches.some((m) => m.isMerged);

  return (
    <div className={cn('flex h-full overflow-hidden', bgPage)}>
      <ScrollArea className="flex-1 min-w-0">
        <div className="p-6 max-w-4xl">
          {/* Header */}
          <div className="flex items-center gap-3 mb-4">
            {isEmbedded && props.onClose && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 hover:bg-[#333]"
                onClick={() => {
                  if (editAttachmentLeaveBlocked) {
                    notifyEditAttachmentLeaveBlocked();
                    return;
                  }
                  props.onClose?.();
                }}
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </Button>
            )}
            <Badge variant="outline" className={cn('gap-1.5', borderCls, bgCard)}>
              <CheckSquare className="h-3 w-3" />
              {isCreate ? 'New Task' : 'Task'}
            </Badge>
            {projectName && (
              <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                <Link to={`/projects/${projectId}`} className="hover:text-foreground transition-colors">
                  {projectName}
                </Link>
                {listName && (
                  <>
                    <span>/</span>
                    <Link
                      to={`/projects/${projectId}/lists/${listId}`}
                      className="hover:text-foreground transition-colors"
                    >
                      {listName}
                    </Link>
                  </>
                )}
              </div>
            )}
            <div className="flex-1" />
            {!isCreate && (
              <>
                {/* Follow / Following toggle */}
                <Button
                  variant={task?.isFollowing ? 'default' : 'outline'}
                  size="sm"
                  className={cn(
                    'h-8 gap-2',
                    task?.isFollowing
                      ? 'bg-primary/90 hover:bg-primary'
                      : cn(borderCls, bgCard, 'hover:bg-[#333]')
                  )}
                  onClick={() => {
                    if (!task) return;
                    if (task.isFollowing) {
                      unfollowTask.mutate(task.id);
                    } else {
                      followTask.mutate(task.id);
                    }
                  }}
                  disabled={followTask.isPending || unfollowTask.isPending}
                >
                  {(followTask.isPending || unfollowTask.isPending) ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : task?.isFollowing ? (
                    <BellRing className="h-4 w-4" />
                  ) : (
                    <Bell className="h-4 w-4" />
                  )}
                  {task?.isFollowing ? 'Following' : 'Follow'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className={cn('h-8 gap-2', borderCls, bgCard, 'hover:bg-[#333]')}
                  onClick={handleShareLink}
                >
                  {linkCopied ? (
                    <>
                      <Check className="h-4 w-4 text-green-500" />
                      <span className="text-green-500">Link copied!</span>
                    </>
                  ) : (
                    <>
                      <Share2 className="h-4 w-4" />
                      Share
                    </>
                  )}
                </Button>
                {isEmbedded && task && (
                  <Button
                    variant="outline"
                    size="icon"
                    className={cn('h-8 w-8', borderCls, bgCard, 'hover:bg-[#333]')}
                    title="Open full screen"
                    aria-label="Open full screen"
                    asChild
                  >
                    <Link
                      to={`/tasks/${task.id}`}
                      onClick={(e) => {
                        if (editAttachmentLeaveBlocked) {
                          e.preventDefault();
                          notifyEditAttachmentLeaveBlocked();
                          return;
                        }
                        props.onClose?.();
                      }}
                    >
                      <ArrowUpRight className="h-4 w-4" />
                    </Link>
                  </Button>
                )}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8 hover:bg-[#333]">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {isEmbedded && (
                      <DropdownMenuItem asChild>
                        <Link
                          to={`/tasks/${task!.id}`}
                          onClick={(e) => {
                            if (editAttachmentLeaveBlocked) {
                              e.preventDefault();
                              notifyEditAttachmentLeaveBlocked();
                              return;
                            }
                            props.onClose?.();
                          }}
                        >
                          <CheckSquare className="h-4 w-4 mr-2" />
                          Open full page
                        </Link>
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onClick={handleShareLink}>
                      <Copy className="h-4 w-4 mr-2" />
                      Copy link
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setShowMoveDialog(true)}>
                      <FolderOutput className="h-4 w-4 mr-2" />
                      Move to...
                    </DropdownMenuItem>
                    {canMerge && (
                      <DropdownMenuItem onClick={() => setShowMergeDialog(true)}>
                        <Merge className="h-4 w-4 mr-2" />
                        Merge into...
                      </DropdownMenuItem>
                    )}
                    {canDeleteTask && (
                      <DropdownMenuItem
                        className="text-destructive focus:text-destructive"
                        onClick={handleDelete}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete task
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
                {!isEmbedded && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 hover:bg-[#333]"
                    aria-label="Close"
                    onClick={() => {
                      if (editAttachmentLeaveBlocked) {
                        notifyEditAttachmentLeaveBlocked();
                        return;
                      }
                      navigate(
                        projectId
                          ? listId
                            ? `/projects/${projectId}/lists/${listId}`
                            : `/projects/${projectId}`
                          : '/dashboard',
                      );
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </>
            )}
            {isCreate && (props as UnifiedTaskEditorCreateProps).onClose && (
              <Button variant="ghost" size="icon" className="h-8 w-8 hover:bg-[#333]" onClick={(props as UnifiedTaskEditorCreateProps).onClose} aria-label="Close">
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>

          {/* Title */}
          <div className="mb-6">
            {isEditingTitle || isCreate ? (
              <Input
                autoFocus
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                onBlur={handleTitleBlur}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleTitleBlur();
                  if (e.key === 'Escape' && !isCreate) {
                    setTitle(task?.title ?? '');
                    setIsEditingTitle(false);
                  }
                }}
                placeholder="Task title"
                className="text-2xl font-semibold border-0 px-0 focus-visible:ring-0 h-auto bg-transparent"
              />
            ) : (
              <h1
                onClick={() => canEdit && setIsEditingTitle(true)}
                className={cn(
                  'text-2xl font-semibold',
                  canEdit && 'cursor-pointer hover:text-muted-foreground transition-colors'
                )}
              >
                {displayTitle}
              </h1>
            )}
          </div>

          {isCreate && duplicateNoticeTitle && normalizedCurrentTitle === duplicateNoticeTitle && (
            <div className={cn('mb-4 rounded-md border px-3 py-2 text-sm', borderCls, bgCard)}>
              <div className="flex items-center justify-between gap-3">
                <p className="text-muted-foreground">
                  {hasMergedDuplicate
                    ? 'A task with this title already existed and was merged into another task.'
                    : 'A task with this title already exists somewhere in this list.'}
                </p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    if (duplicateMatches.length === 0) return;
                    setDuplicateDialogTrigger('blur');
                    setDuplicateDialogOpen(true);
                  }}
                >
                  Review
                </Button>
              </div>
            </div>
          )}

          {/* Inline fields row — same in create and edit */}
          <div className="flex flex-wrap gap-3 mb-6">
            {/* Status */}
            {canEdit ? (
              <Select value={statusId} onValueChange={handleStatusChange}>
                <SelectTrigger className={cn('w-auto h-8 gap-2', borderCls, bgCard, 'hover:bg-[#333] focus:ring-0')}>
                  <CircleDot className="h-4 w-4 text-muted-foreground" />
                  {currentStatus ? (() => {
                    const statusBg = currentStatus.color || '#6b7280';
                    const statusText = getContrastTextColor(statusBg);
                    return (
                      <Badge className="h-6 px-2.5 font-medium" style={{ backgroundColor: statusBg, color: statusText, borderColor: 'transparent' }}>
                        {currentStatus.name}
                      </Badge>
                    );
                  })() : (
                    <span className="text-sm">Status</span>
                  )}
                </SelectTrigger>
                <SelectContent>
                  {sortedStatuses.map((s) => {
                    const sBg = s.color || '#6b7280';
                    const sText = getContrastTextColor(sBg);
                    return (
                      <SelectItem key={s.id} value={s.id}>
                        <Badge className="font-medium" style={{ backgroundColor: sBg, color: sText, borderColor: 'transparent' }}>
                          {s.name}
                        </Badge>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            ) : (
              <div className={cn('flex items-center gap-2 h-8 px-3 rounded-md border', borderCls, bgCard)}>
                <CircleDot className="h-4 w-4 text-muted-foreground" />
                {currentStatus && (() => {
                  const statusBg = currentStatus.color || '#6b7280';
                  const statusText = getContrastTextColor(statusBg);
                  return (
                    <Badge className="h-6 px-2.5 font-medium" style={{ backgroundColor: statusBg, color: statusText, borderColor: 'transparent' }}>
                      {currentStatus.name}
                    </Badge>
                  );
                })()}
              </div>
            )}

            {/* Due date */}
            <div className={cn('flex items-center gap-2 h-8 px-3 rounded-md border', borderCls, bgCard)}>
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Due</span>
              {canEdit ? (
                <Input
                  type="date"
                  value={dueDate}
                  onChange={handleDueDateChange}
                  className="w-auto h-6 border-0 bg-transparent p-0 text-sm focus-visible:ring-0"
                />
              ) : (
                <span className="text-sm">{dueDate ? formatDisplayDate(dueDate) : '—'}</span>
              )}
            </div>

            {/* Assignee(s) */}
            {isCreate ? (
              canAssignTask ? (
                <AssigneePicker
                  listId={listId}
                  currentAssigneeIds={assigneeIds}
                  currentTeamId={assignedTeamId}
                  currentTeamName={undefined}
                  onChange={handleAssigneeChange}
                />
              ) : (
                <div className={cn('flex items-center gap-2 h-8 px-3 rounded-md border', borderCls, bgCard)}>
                  <User className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Unassigned</span>
                </div>
              )
            ) : canEdit && canAssignTask ? (
              <AssigneePicker
                listId={listId}
                currentAssigneeIds={assigneeIds}
                currentTeamId={assignedTeamId}
                currentAssigneeUsers={getTaskAssignees(task!)}
                currentTeamName={task?.assignedTeam?.name}
                onChange={handleAssigneeChange}
              />
            ) : task ? (
              <div className={cn('flex items-center gap-2 min-h-8 px-3 py-1 rounded-md border', borderCls, bgCard, 'min-w-0')}>
                <User className="h-4 w-4 text-muted-foreground shrink-0" />
                <TaskAssigneesCell task={task} variant="compact" className="min-w-0 flex-1" />
              </div>
            ) : (
              <div className={cn('flex items-center gap-2 h-8 px-3 rounded-md border', borderCls, bgCard)}>
                <User className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Unassigned</span>
              </div>
            )}

            {/* Priority */}
            {canEdit ? (
              <Select value={priority} onValueChange={handlePriorityChange}>
                <SelectTrigger className={cn('w-auto h-8 gap-2', borderCls, bgCard, 'hover:bg-[#333] focus:ring-0')}>
                  <Flag className="h-4 w-4 text-muted-foreground" />
                  {(() => {
                    const pBg = getPriorityHexColor(priority);
                    const pText = getContrastTextColor(pBg);
                    return (
                      <Badge className="h-6 capitalize font-medium" style={{ backgroundColor: pBg, color: pText, borderColor: 'transparent' }}>
                        {priority}
                      </Badge>
                    );
                  })()}
                </SelectTrigger>
                <SelectContent>
                  {['urgent', 'high', 'medium', 'low'].map((p) => {
                    const pBg = getPriorityHexColor(p);
                    const pText = getContrastTextColor(pBg);
                    return (
                      <SelectItem key={p} value={p}>
                        <Badge className="capitalize font-medium" style={{ backgroundColor: pBg, color: pText, borderColor: 'transparent' }}>{p}</Badge>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            ) : (
              <div className={cn('flex items-center gap-2 h-8 px-3 rounded-md border', borderCls, bgCard)}>
                <Flag className="h-4 w-4 text-muted-foreground" />
                {(() => {
                  const pBg = getPriorityHexColor(priority);
                  const pText = getContrastTextColor(pBg);
                  return (
                    <Badge className="h-6 capitalize font-medium" style={{ backgroundColor: pBg, color: pText, borderColor: 'transparent' }}>{priority}</Badge>
                  );
                })()}
              </div>
            )}

            {/* Tags */}
            <div className={cn('flex items-center gap-2 min-h-[32px] px-3 rounded-md border', borderCls, bgCard)}>
              <Tag className="h-4 w-4 text-muted-foreground shrink-0" />
              {isCreate ? (
                <TagPicker
                  listId={listId}
                  draftMode
                  currentTags={createDraftTags}
                  onDraftTagsChange={setCreateDraftTags}
                  disabled={!canEdit || !canApplyTags}
                />
              ) : (
                <TagPicker
                  taskId={task!.id}
                  listId={listId}
                  currentTags={task!.tags ?? []}
                  disabled={!canEdit}
                />
              )}
            </div>
          </div>

          {/* Description */}
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-2 text-sm text-muted-foreground">Add description</div>
            {isEditingDescription || isCreate ? (
              <Textarea
                autoFocus={!isCreate}
                placeholder="Add a more detailed description..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                onBlur={handleDescriptionBlur}
                onKeyDown={(e) => {
                  if (e.key === 'Escape' && !isCreate) {
                    setDescription(task?.description || '');
                    setIsEditingDescription(false);
                  }
                }}
                className={cn('min-h-[120px] resize-none', bgCard, borderCls)}
              />
            ) : (
              <div
                onClick={() => canEdit && setIsEditingDescription(true)}
                className={cn(
                  'min-h-[80px] rounded-lg border px-3 py-2 text-sm whitespace-pre-wrap',
                  borderCls,
                  bgCard,
                  canEdit && 'cursor-pointer hover:bg-[#333] transition-colors',
                  !description && 'text-muted-foreground'
                )}
              >
                {description || 'Add a more detailed description...'}
              </div>
            )}
          </div>

          {/* Visibility info */}
          {!isClientVisible && (
            <div className={cn('mb-6 rounded-lg px-4 py-3 border', bgCard, borderCls)}>
              <p className="text-sm text-muted-foreground">This task is only visible to admin and user roles.</p>
            </div>
          )}

          {/* Fields collapsible */}
          <Collapsible open={fieldsOpen} onOpenChange={setFieldsOpen} className="mb-6">
            <CollapsibleTrigger asChild>
              <div className="flex items-center justify-between py-2 cursor-pointer group">
                <div className="flex items-center gap-2">
                  <ChevronDown className={cn('h-4 w-4 transition-transform', !fieldsOpen && '-rotate-90')} />
                  <span className="text-sm font-medium">Fields</span>
                </div>
                {!isCreate && (canManageListCustomFields || canManageProjectCustomFields) && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-7 gap-1 text-xs text-muted-foreground hover:text-foreground"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Plus className="h-3.5 w-3.5" />
                        Add field
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                      {canManageListCustomFields && (
                        <>
                          <DropdownMenuItem onClick={() => handleCreateCustomField('list', 'text')}>
                            New list text field
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleCreateCustomField('list', 'longText')}
                          >
                            New list long text field
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleCreateCustomField('list', 'number')}>
                            New list number field
                          </DropdownMenuItem>
                        </>
                      )}
                      {canManageProjectCustomFields && (
                        <>
                          <DropdownMenuItem onClick={() => handleCreateCustomField('project', 'text')}>
                            New project text field
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleCreateCustomField('project', 'longText')}
                          >
                            New project long text field
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleCreateCustomField('project', 'number')}
                          >
                            New project number field
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className={cn('rounded-lg border overflow-hidden mt-2', borderCls, bgCard)}>
                <div className={cn('flex items-center px-4 py-3 border-b', borderCls)}>
                  <div className="flex items-center gap-3 w-40 shrink-0">
                    {isClientVisible ? (
                      <Eye className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <EyeOff className="h-4 w-4 text-muted-foreground" />
                    )}
                    <span className="text-sm text-muted-foreground">Visibility</span>
                  </div>
                  {canEdit && (
                    <Button variant="ghost" size="sm" onClick={handleVisibilityToggle} className="h-7 gap-2">
                      <Badge variant={isClientVisible ? 'default' : 'secondary'}>
                        {isClientVisible ? 'Client Visible' : 'Internal'}
                      </Badge>
                    </Button>
                  )}
                </div>
                {!isCreate && task?.creator && (
                  <div className={cn('flex items-center px-4 py-3', borderCls, 'border-b')}>
                    <div className="flex items-center gap-3 w-40 shrink-0">
                      <User className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">Created by</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Avatar className="h-5 w-5">
                        <AvatarImage src={task.creator.avatarUrl || undefined} />
                        <AvatarFallback className="text-[8px]">{getInitials(task.creator.displayName)}</AvatarFallback>
                      </Avatar>
                      <span className="text-sm">{task.creator.displayName}</span>
                    </div>
                  </div>
                )}
                {isCreate && listId && canEditTaskCustomFields ? (
                  <CustomFieldsSection
                    listId={listId}
                    canEdit={canEditTaskCustomFields}
                    draftValues={createCustomFieldValues}
                    onDraftChange={(key, value) =>
                      setCreateCustomFieldValues((prev) => ({ ...prev, [key]: value }))
                    }
                  />
                ) : (
                  !isCreate &&
                  task && (
                    <CustomFieldsSection
                      taskId={task.id}
                      listId={task.listId}
                      canEdit={canEdit && canEditTaskCustomFields}
                    />
                  )
                )}
              </div>
            </CollapsibleContent>
          </Collapsible>

          {/* Subtasks (parent task only) */}
          {!isCreate && task && (task.subtasks !== undefined || (task.restrictedSubtaskCount ?? 0) > 0) && (
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3">
                <ListTodo className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">Subtasks</span>
              </div>
              <div className={cn('rounded-lg border overflow-hidden', borderCls, bgCard)}>
                {(task.subtasks?.length ?? 0) > 0 &&
                  task.subtasks!.map((st) => (
                    <Link
                      key={st.id}
                      to={`/tasks/${st.id}`}
                      className={cn(
                        'flex items-center gap-3 px-4 py-2.5 hover:bg-[#333] transition-colors border-b border-[#444] last:border-b-0',
                        'text-left'
                      )}
                    >
                      <span className="text-sm font-medium truncate flex-1">{st.title}</span>
                      {st.status && (() => {
                        const stBg = st.status.color || '#6b7280';
                        const stText = getContrastTextColor(stBg);
                        return (
                          <Badge variant="secondary" className="text-xs shrink-0 font-medium" style={{ backgroundColor: stBg, color: stText, borderColor: 'transparent' }}>
                            {st.status.name}
                          </Badge>
                        );
                      })()}
                      {getTaskAssignees(st).length > 0 && (
                        <div className="flex -space-x-1 shrink-0">
                          {getTaskAssignees(st)
                            .slice(0, 3)
                            .map((a) => (
                              <Avatar key={a.id} className="h-5 w-5 border border-background">
                                <AvatarImage src={a.avatarUrl ?? undefined} alt={a.displayName} />
                                <AvatarFallback className="text-[8px]">{getInitials(a.displayName)}</AvatarFallback>
                              </Avatar>
                            ))}
                          {getTaskAssignees(st).length > 3 && (
                            <div className="h-5 w-5 rounded-full border border-background bg-muted flex items-center justify-center text-[8px] text-muted-foreground">
                              +{getTaskAssignees(st).length - 3}
                            </div>
                          )}
                        </div>
                      )}
                      {st.dueDate && (
                        <span className="text-xs text-muted-foreground shrink-0">
                          {formatDisplayDate(st.dueDate)}
                        </span>
                      )}
                      {st.list && (
                        <Badge variant="outline" className="text-xs shrink-0">{st.list.name}</Badge>
                      )}
                    </Link>
                  ))}
                {(task.restrictedSubtaskCount ?? 0) > 0 && (
                  <div className="px-4 py-2.5 text-sm text-muted-foreground border-t border-[#444]">
                    {task.restrictedSubtaskCount} restricted subtask{task.restrictedSubtaskCount === 1 ? '' : 's'}
                  </div>
                )}
                {(!task.subtasks?.length && (task.restrictedSubtaskCount ?? 0) === 0) && (
                  <div className="px-4 py-3 text-sm text-muted-foreground">No subtasks</div>
                )}
              </div>
            </div>
          )}

          {/* Pending attachments (create only) */}
          {isCreate && canUploadAttachments && (
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-2">
                <Paperclip className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">Attachments</span>
                <span className="text-xs text-muted-foreground">(uploaded after you save)</span>
              </div>
              <input
                ref={pendingFilesInputRef}
                type="file"
                multiple
                className="hidden"
                accept={ATTACHMENT_FILE_INPUT_ACCEPT}
                onChange={handlePendingFilesSelected}
              />
              <div className="flex flex-wrap gap-2 mb-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="gap-2"
                  disabled={createTask.isPending || isUploadingPendingFiles}
                  onClick={() => pendingFilesInputRef.current?.click()}
                >
                  <Paperclip className="h-4 w-4" />
                  Add files
                </Button>
              </div>
              {pendingAttachmentFiles.length > 0 && (
                <div className={cn('rounded-lg border overflow-hidden', borderCls, bgCard)}>
                  {pendingAttachmentFiles.map((file, i) => (
                    <div
                      key={`${file.name}-${i}-${file.size}`}
                      className={cn(
                        'flex items-center justify-between gap-2 px-3 py-2 text-sm',
                        i < pendingAttachmentFiles.length - 1 && 'border-b border-[#444]'
                      )}
                    >
                      <span className="truncate min-w-0">{file.name}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 shrink-0"
                        disabled={createTask.isPending || isUploadingPendingFiles}
                        onClick={() => removePendingFile(i)}
                        title="Remove"
                      >
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Actions: Save (create) or action buttons (edit) */}
          {isCreate ? (
            <div className="flex gap-2 mb-6">
              <Button
                onClick={handleSaveCreate}
                disabled={
                  createTask.isPending || isUploadingPendingFiles || isCheckingDuplicates || !title.trim()
                }
              >
                {(createTask.isPending || isUploadingPendingFiles) && (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                )}
                {isUploadingPendingFiles
                  ? 'Uploading attachments...'
                  : createTask.isPending
                    ? 'Creating...'
                    : 'Create task'}
              </Button>
              {props.onClose && (
                <Button variant="outline" onClick={props.onClose}>
                  Cancel
                </Button>
              )}
            </div>
          ) : (
            canEdit && (
              <div className="flex flex-wrap gap-2 mb-6 text-muted-foreground">
                {canCreateSubtask && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 gap-2 hover:bg-[#333]"
                    onClick={() => setAddSubtaskOpen(true)}
                  >
                    <ListTodo className="h-4 w-4" />
                    Add subtask
                  </Button>
                )}
                <Button variant="ghost" size="sm" className="h-8 gap-2 hover:bg-[#333]">
                  <Link2 className="h-4 w-4" />
                  Relate items or add dependencies
                </Button>
                <Button variant="ghost" size="sm" className="h-8 gap-2 hover:bg-[#333]">
                  <CheckCheck className="h-4 w-4" />
                  Create checklist
                </Button>
                <label
                  className={cn(
                    'cursor-pointer',
                    (uploadAttachment.isPending || isUploadingEditAttachments) && 'pointer-events-none opacity-70',
                  )}
                >
                  <input
                    type="file"
                    multiple
                    className="hidden"
                    accept={ATTACHMENT_FILE_INPUT_ACCEPT}
                    onChange={handleFileUpload}
                    disabled={uploadAttachment.isPending || isUploadingEditAttachments}
                  />
                  <Button variant="ghost" size="sm" className="h-8 gap-2 hover:bg-[#333]" asChild>
                    <span>
                      {uploadAttachment.isPending || isUploadingEditAttachments ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Paperclip className="h-4 w-4" />
                      )}
                      {uploadAttachment.isPending || isUploadingEditAttachments ? 'Uploading…' : 'Attach file'}
                    </span>
                  </Button>
                </label>
              </div>
            )
          )}

          {/* Attachments */}
          {!isCreate && (
            <>
              {(isUploadingEditAttachments || uploadAttachment.isPending) && editUploadBatchTotal > 0 && (
                <div
                  className={cn(
                    'mb-4 flex items-start gap-3 rounded-lg border px-4 py-3',
                    borderCls,
                    'bg-muted/40',
                  )}
                  role="status"
                  aria-live="polite"
                >
                  <Loader2 className="h-5 w-5 shrink-0 animate-spin text-muted-foreground mt-0.5" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium">
                      Uploading {editUploadBatchTotal} file{editUploadBatchTotal === 1 ? '' : 's'}…
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      You can keep working; attachments appear below when each file finishes.
                    </p>
                  </div>
                </div>
              )}
              {attachments.length > 0 && (
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-3">
                    <Paperclip className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">Attachments</span>
                    <span className="text-xs text-muted-foreground">({attachments.length})</span>
                    {(uploadAttachment.isPending || isUploadingEditAttachments) && (
                      <>
                        <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
                        <span className="text-xs text-muted-foreground">Uploading…</span>
                      </>
                    )}
                  </div>
                  <div className={cn('rounded-lg border overflow-hidden', borderCls, bgCard)}>
                    {attachments.map((att, i) => (
                      <div
                        key={att.id}
                        className={cn(
                          'flex items-center gap-3 px-4 py-2.5 hover:bg-[#333] transition-colors group',
                          i < attachments.length - 1 && 'border-b border-[#444]'
                        )}
                      >
                        <Paperclip className="h-4 w-4 text-muted-foreground shrink-0" />
                        <div className="flex-1 min-w-0">
                          <button
                            type="button"
                            className="text-sm truncate block max-w-full text-left hover:underline cursor-pointer"
                            onClick={() => handleFileDownload(att)}
                            title={`Download ${att.fileName}`}
                          >
                            {att.fileName}
                          </button>
                          <p className="text-xs text-muted-foreground">
                            {formatFileSize(att.fileSize)} · {formatRelativeTime(att.createdAt)}
                          </p>
                        </div>
                        <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => handleFileDownload(att)}
                            title="Download"
                          >
                            <Download className="h-3.5 w-3.5" />
                          </Button>
                          {canEdit && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7"
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteAttachment.mutate({ id: att.id, taskId: task!.id });
                              }}
                            >
                              <Trash2 className="h-3.5 w-3.5 text-destructive" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {attachments.length === 0 && canEdit && (
                <label className="cursor-pointer block mb-6">
                  <input
                    type="file"
                    multiple
                    className="hidden"
                    accept={ATTACHMENT_FILE_INPUT_ACCEPT}
                    onChange={handleFileUpload}
                    disabled={uploadAttachment.isPending || isUploadingEditAttachments}
                  />
                  <div
                    className={cn(
                      'rounded-lg border border-dashed px-4 py-6 text-center hover:bg-[#333] transition-colors',
                      borderCls,
                      bgCard,
                      (uploadAttachment.isPending || isUploadingEditAttachments) && 'pointer-events-none opacity-70',
                    )}
                  >
                    {uploadAttachment.isPending || isUploadingEditAttachments ? (
                      <>
                        <Loader2 className="h-6 w-6 mx-auto text-muted-foreground mb-2 animate-spin" />
                        <p className="text-sm text-muted-foreground">
                          {editUploadBatchTotal > 0
                            ? `Uploading ${editUploadBatchTotal} file${editUploadBatchTotal === 1 ? '' : 's'}…`
                            : 'Uploading files…'}
                        </p>
                      </>
                    ) : (
                      <>
                        <Upload className="h-6 w-6 mx-auto text-muted-foreground mb-2" />
                        <p className="text-sm text-muted-foreground">Drop files here or click to upload</p>
                      </>
                    )}
                  </div>
                </label>
              )}
            </>
          )}
        </div>
      </ScrollArea>

      {/* Right panel — Activity & Comments (same layout in both modes) */}
      <div className="w-[380px] shrink-0 flex flex-col border-l border-[#333] bg-[#0f0f0f]">
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#333]">
          <h3 className="text-sm font-medium">Activity</h3>
          <Button variant="ghost" size="icon" className="h-7 w-7 hover:bg-[#333]">
            <Bell className="h-4 w-4" />
          </Button>
        </div>
        <ScrollArea className="flex-1">
          <div className="p-4 space-y-4">
            {!isCreate && task?.creator && (
              <div className="flex gap-3 text-sm">
                <Avatar className="h-7 w-7 shrink-0">
                  <AvatarImage src={task.creator.avatarUrl || undefined} />
                  <AvatarFallback className="text-[10px] bg-primary/20">{getInitials(task.creator.displayName)}</AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p>
                    <span className="font-medium">{task.creator.displayName}</span>
                    <span className="text-muted-foreground"> created this task</span>
                  </p>
                  <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                    <Clock className="h-3 w-3" />
                    {formatActivityTimestamp(task.createdAt)}
                  </p>
                </div>
              </div>
            )}
            {isCreate ? (
              <p className="text-sm text-muted-foreground text-center py-4">Save task to add comments</p>
            ) : (
              <TaskComments taskId={task!.id} comments={comments} history={history} />
            )}
          </div>
        </ScrollArea>
      </div>

      {/* Add subtask dialog */}
      <Dialog open={addSubtaskOpen} onOpenChange={setAddSubtaskOpen}>
        <DialogContent className="sm:max-w-md bg-[#1a1a1a] border-[#333]">
          <DialogHeader>
            <DialogTitle>Add subtask</DialogTitle>
            <DialogDescription>Create a subtask under this ticket with optional list, status, and assignees.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-sm font-medium text-muted-foreground">Title</label>
              <Input
                className="mt-1 bg-[#2a2a2a] border-[#444]"
                value={subtaskTitle}
                onChange={(e) => setSubtaskTitle(e.target.value)}
                placeholder="Subtask title"
              />
            </div>
            {projectLists.length > 1 && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">List</label>
                <Select value={subtaskListId} onValueChange={(v) => { setSubtaskListId(v); setSubtaskStatusId(''); }}>
                  <SelectTrigger className="mt-1 bg-[#2a2a2a] border-[#444]">
                    <span>{projectLists.find((l) => l.id === subtaskListId)?.name ?? 'Select list'}</span>
                  </SelectTrigger>
                  <SelectContent>
                    {projectLists.map((l) => (
                      <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {sortedSubtaskStatuses.length > 0 && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Status</label>
                <Select value={subtaskStatusId} onValueChange={setSubtaskStatusId}>
                  <SelectTrigger className="mt-1 bg-[#2a2a2a] border-[#444]">
                    <span>{sortedSubtaskStatuses.find((s) => s.id === subtaskStatusId)?.name ?? 'Select status'}</span>
                  </SelectTrigger>
                  <SelectContent>
                    {sortedSubtaskStatuses.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        <span className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: s.color }} />
                          {s.name}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <label className="text-sm font-medium text-muted-foreground">Priority</label>
              <Select value={subtaskPriority} onValueChange={(v) => setSubtaskPriority(v as TaskPriority)}>
                <SelectTrigger className="mt-1 bg-[#2a2a2a] border-[#444]">
                  <span className="capitalize">{subtaskPriority}</span>
                </SelectTrigger>
                <SelectContent>
                  {(['urgent', 'high', 'medium', 'low'] as const).map((p) => (
                    <SelectItem key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Assignee</label>
              <div className="mt-1">
                <AssigneePicker
                  listId={subtaskListId || listId}
                  currentAssigneeIds={subtaskAssigneeIds}
                  currentTeamId={subtaskAssignedTeamId}
                  onChange={({ assigneeIds: nextIds, assignedTeamId: nextTeamId }) => {
                    setSubtaskAssigneeIds(nextIds);
                    setSubtaskAssignedTeamId(nextTeamId);
                  }}
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Due date</label>
              <Input
                type="date"
                className="mt-1 bg-[#2a2a2a] border-[#444]"
                value={subtaskDueDate}
                onChange={(e) => setSubtaskDueDate(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddSubtaskOpen(false)}>Cancel</Button>
            <Button onClick={handleAddSubtask} disabled={createSubtask.isPending || !subtaskTitle.trim()}>
              {createSubtask.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {createSubtask.isPending ? 'Creating...' : 'Create subtask'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Duplicate task warning dialog */}
      {isCreate && (
        <Dialog open={duplicateDialogOpen} onOpenChange={setDuplicateDialogOpen}>
          <DialogContent className="sm:max-w-2xl bg-[#1a1a1a] border-[#333]">
            <DialogHeader>
              <DialogTitle>Possible duplicate task</DialogTitle>
              <DialogDescription>
                {hasMergedDuplicate
                  ? 'A task with this title already existed and was merged into another task.'
                  : 'A task with this title already exists somewhere in this list.'}
              </DialogDescription>
            </DialogHeader>

            <div className="max-h-[45vh] overflow-y-auto rounded-md border border-[#333]">
              {duplicateMatches.map((match) => {
                const isSelected = selectedDuplicateTaskId === match.id;
                return (
                  <button
                    key={match.id}
                    type="button"
                    onClick={() => setSelectedDuplicateTaskId(match.id)}
                    className={cn(
                      'w-full px-3 py-2 text-left border-b border-[#333] last:border-b-0 hover:bg-[#2a2a2a] transition-colors',
                      isSelected && 'bg-primary/10',
                    )}
                  >
                    <div className="flex items-center gap-2">
                      <div className="font-medium truncate">{match.title}</div>
                      <Badge
                        className="h-6 px-2.5 font-medium shrink-0"
                      >
                        {match.status.name}
                      </Badge>
                    </div>
                    <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
                      {match.assignee ? (
                        <span className="flex items-center gap-1">
                          <Avatar className="h-4 w-4">
                            <AvatarImage src={match.assignee.avatarUrl || undefined} />
                            <AvatarFallback className="text-[8px]">
                              {getInitials(match.assignee.displayName)}
                            </AvatarFallback>
                          </Avatar>
                          {match.assignee.displayName}
                        </span>
                      ) : (
                        <span>Unassigned</span>
                      )}
                      <span>•</span>
                      <span>
                        Updated{' '}
                        {formatRelativeTime(match.effectiveLastUpdated ?? match.updatedAt)}
                      </span>
                    </div>
                    {match.isMerged && (
                      <div className="mt-1 text-xs text-amber-300">
                        {match.mergedInto
                          ? `Merged into: ${match.mergedInto.title}`
                          : 'Merged into another task (target unavailable)'}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setDuplicateDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                variant="secondary"
                onClick={handleCreateAnyway}
                disabled={createTask.isPending || isUploadingPendingFiles}
              >
                Create anyway
              </Button>
              <Button
                onClick={handleOpenExistingTask}
                disabled={!selectedDuplicateTargetTaskId}
              >
                {selectedDuplicateMatch?.isMerged
                  ? 'Open merged target task'
                  : 'Open existing task'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Edit-mode attachment upload safety (modal + leave guards; TaskPanel backdrop uses ui-store barrier). */}
      {!isCreate && (
        <Dialog open={editAttachmentUploadModal !== null} onOpenChange={() => {}}>
          <DialogContent
            className={cn(
              'z-[200] gap-4 border-[#333] bg-[#1a1a1a] sm:max-w-md',
              '[&>button.absolute]:hidden',
            )}
            onPointerDownOutside={(e) => e.preventDefault()}
            onEscapeKeyDown={(e) => e.preventDefault()}
          >
            {editAttachmentUploadModal?.phase === 'uploading' && (
              <DialogHeader>
                <div className="flex items-center gap-3">
                  <Loader2 className="h-6 w-6 shrink-0 animate-spin text-muted-foreground" />
                  <DialogTitle>Uploading attachment</DialogTitle>
                </div>
                <DialogDescription className="text-left whitespace-pre-line pt-1">
                  {`Uploading ${editAttachmentUploadModal.total} file(s)…\n\nPlease do not close this task until the upload is complete.`}
                </DialogDescription>
              </DialogHeader>
            )}
            {editAttachmentUploadModal?.phase === 'result' && (() => {
              const { total, succeeded, failed, failureHint } = editAttachmentUploadModal;
              const allOk = failed === 0;
              const allFailed = succeeded === 0;
              return (
                <>
                  <DialogHeader>
                    <DialogTitle>
                      {allOk
                        ? `Upload complete. ${succeeded} file(s) attached.`
                        : allFailed
                          ? 'Upload not completed'
                          : 'Upload finished with errors'}
                    </DialogTitle>
                    <DialogDescription className="text-left whitespace-pre-line pt-1">
                      {allOk ? (
                        <span className="sr-only">Attachment upload finished successfully.</span>
                      ) : allFailed ? (
                        failureHint ?? 'Please try again.'
                      ) : (
                        <>
                          {`${succeeded} of ${total} file(s) uploaded. ${failed} failed.`}
                          {failureHint ? `\n\n${failureHint}` : ''}
                        </>
                      )}
                    </DialogDescription>
                  </DialogHeader>
                  <DialogFooter className="sm:justify-end">
                    <Button type="button" onClick={() => setEditAttachmentUploadModal(null)}>
                      Continue
                    </Button>
                  </DialogFooter>
                </>
              );
            })()}
          </DialogContent>
        </Dialog>
      )}

      {/* Move Task Dialog */}
      {!isCreate && task && (
        <MoveTaskDialog
          task={task}
          open={showMoveDialog}
          onOpenChange={setShowMoveDialog}
          onMoved={(result) => {
            if (editAttachmentLeaveBlocked) {
              notifyEditAttachmentLeaveBlocked();
              return;
            }
            if (result.lostAccess) {
              navigate(projectId ? `/projects/${projectId}` : '/dashboard');
            } else {
              navigate(
                `/projects/${result.targetProjectId}/lists/${result.targetListId}?taskId=${task.id}`,
              );
            }
          }}
        />
      )}

      {/* Merge Ticket Dialog */}
      {!isCreate && task && (
        <MergeTicketDialog
          task={task}
          open={showMergeDialog}
          onOpenChange={setShowMergeDialog}
          onMerged={(result) => {
            if (editAttachmentLeaveBlocked) {
              notifyEditAttachmentLeaveBlocked();
              return;
            }
            props.onClose?.();
            navigate(`/projects/${projectId}/lists/${listId}?taskId=${result.targetTaskId}`);
          }}
        />
      )}
    </div>
  );
}
